<script setup lang="ts">
import Chart from "chart.js/auto";
import type {
  ChartItem,
  ChartConfiguration,
  ChartData,
  ChartOptions,
} from "chart.js/auto";
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, type PropType, ref, watch } from "vue";
import { useAnalytic } from "@/stores/analysis";
import { useI18n } from "vue-i18n";
import ChartColors from "@/components/analysis/composables/chartColors";
import legendMargin from "@/components/analysis/composables/legendMargin";
import ChartCheckBox from "@/components/analysis/ChartCheckBox.vue";
import Loading from "vue3-loading-overlay";
import numberFormat from "@/utils/numberFormat";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { ChartData as ChartItemType } from "@/stores/comparasion";


const analytic = useAnalytic();
const { t } = useI18n();

const props = defineProps({
  propsData: {
    type: Object,
    required: false,
  },
  chartId: {
    type: String,
    required: false,
  },
  company: {
    type: String,
    required: false,
  },
  isLimit: {
    type: Boolean,
    required: false,
    default: true
  },
  limit: {
    type: Number,
    required: false,
    default: 20
  },
  breakDown: {
    type: String,
    required: false
  },
  graphTotal: {
    type: Number,
    required: false,
  },
});

let myChart: any = null;

const isLoading = computed(() => {
  const hasPropsData = props.propsData &&
    (Array.isArray(props.propsData) ? props.propsData.length > 0 : Object.keys(props.propsData).length > 0);

  return !hasPropsData && analytic.dataList.length === 0;
});

onMounted(async () => {
  await nextTick();
  destroyChart()
  generateChart()
  initCheckedValue()
})

const destroyChart = () => {
  if (myChart) {
    myChart.destroy();
    myChart = null;
  }
};

const effectiveBreakdown = computed(() => {
  return props.breakDown ? props.breakDown : analytic.breakdown;
});

const legendItems = computed(() => {
  let selection = props.propsData ? props.propsData : analytic.dataList;

  if (!props.isLimit) { return selection }

  //@ts-ignore
  return selection.sort((a: ChartItemType, b: ChartItemType) => { b.value - a.value }).slice(0, props.limit);
})

const chartData = (): ChartData => {
  let labels: string[] = [];
  let data: number[] = [];

  let selection: any[] = Array.isArray(props.propsData)
    ? props.propsData
    : (props.propsData ? Object.values(props.propsData) : analytic.dataList);

  let backgroundColor: string[] = ChartColors(effectiveBreakdown.value, selection as ChartItemType[]);


  if (props.isLimit) {
    selection = selection.sort((a: ChartItemType, b: ChartItemType) => b.value - a.value).slice(0, props.limit)
  }

  selection.forEach((item: any) => {
    if (effectiveBreakdown.value === 'SCOPE') {
      labels.push(t(`analysis.scope.${item.label}`))
    } else {
      labels.push(item.label.slice(0, 26) + ' ' + numberFormat(item.percentage) + '%')
    }
    data.push(item.value);
  });

  return {
    labels,
    datasets: [
      {
        backgroundColor,
        data,
      },
    ],
  };
};

const centerText: any = {
  id: "centerText",
  beforeDraw(chart: any, args: any, options: any) {
    const {
      ctx,
      chartArea: { top, right, bottom, left, width, height },
    } = chart;
    ctx.save();

    let unit = analytic.responseUnit || "tCO2e";

    const total = !isNaN(props.graphTotal ?? NaN) ? props.graphTotal ?? 0 : analytic.total;

    const text =
      total !== 0 ? numberFormat(total) + ` ${unit}` : "No data";

    if (text.length <= 18) {
      ctx.font = "26px sans-serif";
    } else {
      ctx.font = "18px sans-serif";
    }

    ctx.fontWeight = "900";
    ctx.textAlign = "center";

    ctx.fillText(text, width / 2, top + height / 2);
  },
};

// Generate the chart
let generateChart = () => {
  destroyChart();
  const id = props.chartId || "myChart";
  const ctx = <ChartItem>document.getElementById(id);

  const chartWithKey = Chart.getChart(id);

  Chart.unregister(ChartDataLabels);

  if (chartWithKey != undefined || chartWithKey) {
    chartWithKey.destroy();
  }

  const options: ChartOptions<"doughnut"> = {
    locale: "es-MX",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 0,
    cutout: 120,
    plugins: {
      legend: {
        position: effectiveBreakdown.value === "SCOPE" ? "bottom" : "right",
        display: false,
        fullSize: false,
        labels: {
          color: "#212121",
          font: {
            size: 14,
            family: "sans-serif",
          },
        },
        title: {
          display: effectiveBreakdown.value !== "SCOPE",
          color: "#757575",
          font: {
            size: 14,
            family: "Lufga",
            weight: "normal",
          },
          text: t(`analysis.${effectiveBreakdown.value}`),
          position: "start",
          padding: 10,
        },
      },
    },
  };
  const config: ChartConfiguration = {
    type: "doughnut",
    data: chartData(),
    options: <ChartOptions>options,
    plugins: [centerText, legendMargin()],
  };

  myChart = new Chart(ctx, config);
};

// Watches and interactions
watch(
  () => JSON.parse(JSON.stringify(analytic.dataList)),
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      generateChart();
      initCheckedValue();
    }
  },
  { deep: false }
);


watch(
  () => JSON.parse(JSON.stringify(props.propsData || {})),
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      generateChart();
      initCheckedValue();
    }
  },
  { deep: false }
);

let selectChart = ref([]);

const initCheckedValue = () => {
  selectChart.value = myChart.data.labels.map((el: any, i: any) => i);
};

const updateChart = (index: number) => {
  const isDataShow = selectChart.value.indexOf(<never>index);

  if (isDataShow === -1) {
    myChart.hide(0, index);
  } else {
    myChart.show(0, index);
  }
};

const legendColor = (index: number) => {
  if (myChart === null) return "#000";
  const color: string = myChart.data.datasets[0].backgroundColor.filter(
    (x: any, i: number) => i === index
  )[0];
  return color;
};
</script>


<template>
  <div class="text-center relative pt-10 flex flex-col">
    <div class="relative">
      <canvas :id="props.chartId || 'myChart'" width="400" height="400"></canvas>
    </div>
    <div class="flex flex-wrap text-center justify-center items-center relative">
      <Loading :active="isLoading" :is-full-page="false" :width="32" :height="32" />
      <ChartCheckBox v-for="(legend, index) in legendItems" v-model:selected="selectChart" :key="index"
        :id="`chart_${index}`" :name="`chart_${index}`" :value="index" :style="{ '--bgColor': legendColor(index) }"
        :bg-color="legendColor(index)" @change="updateChart(index)">
        <span class="ml-2" v-if="effectiveBreakdown === 'SCOPE'">{{ `${t('analysis.SCOPE')} ${legend.label}` }}
        </span>
        <span v-else class="ml-2">{{ legend.label }}</span>
        {{ legend.percentage }}%
      </ChartCheckBox>
    </div>
  </div>
</template>
